import {useEffect, useState} from "react";
import {ChartDataModel, ChartModel} from "../models/ChartDataModel";
import {DashboardVejledersamtaleModel,} from "./DashboardVejledersamtaleModel";
import {DashboardPieChartModel} from "../models/DashboardPieChartModel";
import {useDashboardContext} from "../dashboardContextProvider";
import {GroupByKeyAndApplyName} from "../dashboardUtils";
import {DashboardColors} from "../models/DashboardColors";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {GetStatusString, VejledersamtaleStatusEnum} from "../../vejledning/vejledersamtaler/VejledersamtaleStatusEnum";
import {VejledersamtaleEnum} from "../../vejledning/vejledersamtaler/vejledersamtaleModel";
import {getEnums} from "../../../core/helpers/enumToList";
import {getMinMaxDage, GetVejledningTimeString, VejledningDatoKategoriEnum} from "./VejledningDatoKategoriEnum";
import {CategoryCounts, DashboardAmountEnum, translateDashboardAmountEnum} from "./DashboardAmountEnum";
import {removeWhiteSpace} from "../../../core/helpers/stringHelper";
import {nameof} from "ts-simple-nameof";
import {DashboardListForloebModel} from "../dashboardList/models/DashboardListForloebModel";
import {InitialFilterModel} from "../../../core/components/reactTable/ReactBaseTable";
import {useHistory} from "react-router-dom";
import {RoutePaths} from "../../../infrastructure/routes";
const useDashboardVejledersamtaler = () => {
    const {state} = useDashboardContext();
    const history = useHistory();

    const [introduktionPieChart, setIntroduktionPieChart] = useState<ChartModel[]>([]);
    const [justerendePieChart, setJusterendePieChart] = useState<ChartModel[]>([]);
    const [afsluttendePieChart, setAfsluttendePieChart] = useState<ChartModel[]>([]);
    const [vejledersamtalerTidSidenSidste, setVejledersamtalerTidSidenSidste] = useState<ChartModel[]>([]);

    const [introduktionTotalAmount, setIntroduktionTotalAmount] = useState<number>(0);
    const [justerendeTotalAmount, setJusterendeTotalAmount] = useState<number>(0);
    const [afsluttendeTotalAmount, setAfsluttendeTotalAmount] = useState<number>(0);
    const [vejledersamtalerTidSidenSidsteTotalAmount, setVejledersamtalerTidSidenSidsteTotalAmount] = useState<number>(0);

    const [justerendeVejledersamtalerBarChart, setJusterendeVejledersamtalerBarChart] = useState<ChartModel>();
    const [showIkkePlanlagte, setShowIkkePlanlagte] = useState<boolean>(true);

    useEffect(() => {
        let vejledersamtaler = state.vejledersamtaler;

        if (!showIkkePlanlagte)
            vejledersamtaler = state.vejledersamtaler.filter(x => x.status !== VejledersamtaleStatusEnum.EndnuIkkePlanlagt)

        const introsamtaler = vejledersamtaler.filter(x => x.vejledersamtaleType === VejledersamtaleEnum.Introduktion);
        const justerendeOgAndenSamtaler = vejledersamtaler.filter(x => x.vejledersamtaleType === VejledersamtaleEnum.Justering || x.vejledersamtaleType === VejledersamtaleEnum.Anden);
        const afsluttendeSamtaler = vejledersamtaler.filter(x => x.vejledersamtaleType === VejledersamtaleEnum.Afsluttende);

        setIntroduktionTotalAmount(introsamtaler.length)
        setJusterendeTotalAmount(justerendeOgAndenSamtaler.length)
        setAfsluttendeTotalAmount(afsluttendeSamtaler.length)

        const getModelValue = (vejledersamtaleType: VejledersamtaleEnum): string => {
            switch (vejledersamtaleType) {
                case VejledersamtaleEnum.Introduktion:
                    return nameof<DashboardListForloebModel>(x => x.introVejledersamtaleStatus).toLowerCase();
                case VejledersamtaleEnum.Justering:
                    return nameof<DashboardListForloebModel>(x => x.justerendeVejledersamtaleStatus).toLowerCase();
                case VejledersamtaleEnum.Afsluttende:
                    return nameof<DashboardListForloebModel>(x => x.afsluttendeVejledersamtaleStatus).toLowerCase();
                default:
                    return "";
            }
        };
        const handleVejledersamtaleTypeClick = (status: string, vejledersamtaleType: VejledersamtaleEnum) => {
            history.push({
                pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                state: [
                    {
                        initialFilterId: getModelValue(vejledersamtaleType),
                        initialFilterValue: status
                    },
                ] as InitialFilterModel[]
            });
        }

        const HandleVejledersamtaleType = (vejledersamtaler: DashboardVejledersamtaleModel[], vejledersamtaleType: VejledersamtaleEnum): ChartModel[] => {
            let pieModels: ChartModel[] = []
            const allStatusEnum = getEnums(VejledersamtaleStatusEnum);
            const vejledersamtalerGrouped = GroupByKeyAndApplyName(vejledersamtaler, x => x.status.toString(), x => GetStatusString(x.status))

            allStatusEnum.forEach(enumValue => {
                if (!vejledersamtalerGrouped[enumValue]) {
                    vejledersamtalerGrouped[enumValue] = {
                        key: enumValue.toString(),
                        name: GetStatusString(enumValue),
                        items: []
                    };
                }
            });

            const vejledersamtalerPieModel = Object.keys(vejledersamtalerGrouped)
                .map(key => ({
                    key: key,
                    name: vejledersamtalerGrouped[key].name,
                    models: vejledersamtalerGrouped[key].items,
                    handleOnClick: () => handleVejledersamtaleTypeClick(vejledersamtalerGrouped[key].name, vejledersamtaleType)
                })) as DashboardPieChartModel<DashboardVejledersamtaleModel>[]
            const vejledersamtalerPieChart = mapToPieChartModel(vejledersamtalerPieModel, Localizer.global_vejledersamtaler())

            pieModels.push(vejledersamtalerPieChart);
            return pieModels;
        }

        setIntroduktionPieChart(HandleVejledersamtaleType(introsamtaler, VejledersamtaleEnum.Introduktion));
        setJusterendePieChart(HandleVejledersamtaleType(justerendeOgAndenSamtaler, VejledersamtaleEnum.Justering));
        setAfsluttendePieChart(HandleVejledersamtaleType(afsluttendeSamtaler, VejledersamtaleEnum.Afsluttende));

        const HandleVejledersamtaleTid = (vejledersamtaler: DashboardVejledersamtaleModel[]): ChartModel[] => {
            const today = new Date();

            //We need to group vejledersamtaler by forloeb - since we only need to check when the last vejledersamtale was for each forloeb
            const vejledersamtalerGrouperByForloeb = GroupByKeyAndApplyName(vejledersamtaler, x => x.forloebId, x => x.forloebId);

            //Sorter vejledersamtaer på afholdtDato og tag den nyeste for hvert forloeb
            const newestVejledersamtaleForEachForloeb = Object.keys(vejledersamtalerGrouperByForloeb)
                .map(key => vejledersamtalerGrouperByForloeb[key].items
                    .sort((a, b) => {
                        if (a.afholdtDato === undefined || a.afholdtDato === null) return 1;
                        if (b.afholdtDato === undefined || b.afholdtDato === null) return -1;
                        return b.afholdtDato.getTime() - a.afholdtDato.getTime();
                    })[0]);

            setVejledersamtalerTidSidenSidsteTotalAmount(newestVejledersamtaleForEachForloeb.length);

            for (let vejledersamtale of newestVejledersamtaleForEachForloeb) {
                if (vejledersamtale.afholdtDato === null || vejledersamtale.afholdtDato === undefined || isNaN(new Date(vejledersamtale.afholdtDato).getTime())) {
                    vejledersamtale.datoKategori = VejledningDatoKategoriEnum.IkkeAfholdt;
                } else if (vejledersamtale.afholdtDato >= today.addMonths(-1)) {
                    vejledersamtale.datoKategori = VejledningDatoKategoriEnum.UnderEnMaaned;
                } else if (vejledersamtale.afholdtDato >= today.addMonths(-3)) {
                    vejledersamtale.datoKategori = VejledningDatoKategoriEnum.OverEnMaaned;
                } else if (vejledersamtale.afholdtDato >= today.addMonths(-6)) {
                    vejledersamtale.datoKategori = VejledningDatoKategoriEnum.OverTreMaaneder;
                } else {
                    vejledersamtale.datoKategori = VejledningDatoKategoriEnum.OverSeksMaaneder;
                }
            }

            let pieModels: ChartModel[] = []

            const allDatoKategoriEnums = getEnums(VejledningDatoKategoriEnum);
            const vejledersamtalerGrouped = GroupByKeyAndApplyName(newestVejledersamtaleForEachForloeb, x => x.datoKategori.toString(), x => GetVejledningTimeString(x.datoKategori));
            allDatoKategoriEnums.forEach(enumValue => {
                if (!vejledersamtalerGrouped[enumValue]) {
                    vejledersamtalerGrouped[enumValue] = {
                        key: enumValue.toString(),
                        name: GetVejledningTimeString(enumValue),
                        items: []
                    };
                }
            });

            const handleSidsteVejledersamtaleClick = (datoCategory: [number | undefined, number | undefined]) => {
                history.push({
                    pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                    state: [
                        {
                            initialFilterId: nameof<DashboardListForloebModel>(x => x.dageSidenSidsteVejledersamtale).toLowerCase(),
                            initialFilterValue: datoCategory
                        },
                    ] as InitialFilterModel[]
                });
            };

            const vejledersamtalerPieModel = Object.keys(vejledersamtalerGrouped)
                .map(key => ({
                    key: key,
                    name: vejledersamtalerGrouped[key].name,
                    models: vejledersamtalerGrouped[key].items,
                    handleOnClick: () => handleSidsteVejledersamtaleClick(getMinMaxDage(vejledersamtalerGrouped[key].items[0].datoKategori))
                })) as DashboardPieChartModel<DashboardVejledersamtaleModel>[]

            const vejledersamtalerPieChart = mapToPieChartModel(vejledersamtalerPieModel, Localizer.global_vejledersamtaler())
            pieModels.push(vejledersamtalerPieChart);
            return pieModels;
        }
        setVejledersamtalerTidSidenSidste(HandleVejledersamtaleTid(state.vejledersamtaler));

        // Justerende vejledersamtaler er både vejledersamtaler af typen "Justerende" og "Anden", da man kun kan oprette én justerende, men kunden anser de øvrigt oprettede som justerende
        const justerendeVejledersamtalerCounts: CategoryCounts[] = [];

        // Group justerende samtaler by brugerId
        const groupedJusterendeOgAndenVejledersamtaler = GroupByKeyAndApplyName(justerendeOgAndenSamtaler, x => x.brugerId, x => x.brugerId);

        const categoryCounts = {
            [DashboardAmountEnum.Nul]: 0,
            [DashboardAmountEnum.EnTilTo]: 0,
            [DashboardAmountEnum.TreTilFire]: 0,
            [DashboardAmountEnum.FemTilSeks]: 0,
            [DashboardAmountEnum.SyvTilTi]: 0,
            [DashboardAmountEnum.OverTi]: 0
        } as { [key: number]: number };

        Object.keys(groupedJusterendeOgAndenVejledersamtaler).forEach(bruger => {
            const count = groupedJusterendeOgAndenVejledersamtaler[bruger].items.filter(x => x.status !== VejledersamtaleStatusEnum.EndnuIkkePlanlagt).length;
            if (count === 0)
                categoryCounts[DashboardAmountEnum.Nul]++;
             else if (count <= 2)
                categoryCounts[DashboardAmountEnum.EnTilTo]++;
             else if (count <= 4)
                categoryCounts[DashboardAmountEnum.TreTilFire]++;
             else if (count <= 6)
                categoryCounts[DashboardAmountEnum.FemTilSeks]++;
             else if (count <= 10)
                categoryCounts[DashboardAmountEnum.SyvTilTi]++;
             else if (count > 10)
                categoryCounts[DashboardAmountEnum.OverTi]++;
        });

        const handleAntalJusterendeSamtalerClick = (minMaxValue: [number | undefined, number | undefined]) => {
            history.push({
                pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                state: [
                    {
                        initialFilterId: nameof<DashboardListForloebModel>(x => x.antalJusterendeVejledersamtaler).toLowerCase(),
                        initialFilterValue: minMaxValue
                    },
                ] as InitialFilterModel[]
            });
        }

        getEnums(DashboardAmountEnum).forEach(enumValue => {
            justerendeVejledersamtalerCounts.push({
                amountGroupCategory: enumValue,
                groupCategoryName: translateDashboardAmountEnum(enumValue),
                count: categoryCounts[enumValue],
                onCellClick: () => handleAntalJusterendeSamtalerClick(getMinMaxValue(enumValue)),
            })
        });

        const justerendeVejledersamtaleCountChart = mapToBarChartModel(justerendeVejledersamtalerCounts, Localizer.dashboard_justerendeVejledersamtaleAmount());
        setJusterendeVejledersamtalerBarChart(justerendeVejledersamtaleCountChart);
    }, [state.vejledersamtaler, showIkkePlanlagte, history])

    const mapToPieChartModel = (chartModels: DashboardPieChartModel<DashboardVejledersamtaleModel>[], pieName: string): ChartModel => {
        let elementsPieOne: ChartDataModel[] = [];
        chartModels.forEach((chartModel, index) => {
            elementsPieOne.push(new ChartDataModel(chartModel.key, chartModel.name, chartModel.models.length, DashboardColors.GetVejledningColor(chartModel.name), chartModel.handleOnClick))
        })
        return {
            name: pieName,
            elements: elementsPieOne
        };
    };

    const mapToBarChartModel = (chartModel: CategoryCounts[], barName: string): ChartModel => {
        let elements: ChartDataModel[] = [];
        chartModel.forEach((chartModel, index) => {
            elements.push(new ChartDataModel(chartModel.amountGroupCategory.toString(), chartModel.groupCategoryName, chartModel.count, DashboardColors.JusterendeVejledersamtaleBarChartColor, chartModel.onCellClick))
        });

        return {
            name: barName,
            elements: elements
        };
    };

    return {
        isLoading: state.vejledersamtalerIsLoading,
        setShowIkkePlanlagte: setShowIkkePlanlagte,
        showIkkePlanlagte: showIkkePlanlagte,

        introduktionPieChart: introduktionPieChart,
        justerendePieChart: justerendePieChart,
        afsluttendePieChart: afsluttendePieChart,
        vejledersamtalerTidSidenSidste: vejledersamtalerTidSidenSidste,

        vejledersamtalerTotalAmount: state.vejledersamtaler.length,
        introduktionTotalAmount: introduktionTotalAmount,
        justerendeTotalAmount: justerendeTotalAmount,
        afsluttendeTotalAmount: afsluttendeTotalAmount,
        vejledersamtalerTidSidenSidsteTotalAmount: vejledersamtalerTidSidenSidsteTotalAmount,

        justerendeVejledersamtalerBarChart: justerendeVejledersamtalerBarChart,
    }
}
export default useDashboardVejledersamtaler;

export const getMinMaxValue = (value: DashboardAmountEnum): [number | undefined, number | undefined] => {
    switch (value) {
        case DashboardAmountEnum.Nul:
            return [0, 0];
        case DashboardAmountEnum.EnTilTo:
            return [1, 2];
        case DashboardAmountEnum.TreTilFire:
            return [3, 4];
        case DashboardAmountEnum.FemTilSeks:
            return [5, 6];
        case DashboardAmountEnum.SyvTilTi:
            return [7, 10];
        case DashboardAmountEnum.OverTi:
            return [10, undefined];
        default:
            return [0, 100];
    }
}
